/**
 * The super-admin contract — the two things a platform owner can do once signed in: list every
 * organisation, and switch into one.
 *
 * There is NO separate platform login. The super admin signs in through the ordinary `/auth/login`
 * (see auth.ts — a `PlatformAdmin` credential entered against an organisation enters that
 * organisation), and the session that comes back is a normal tenant session acting as that
 * organisation's hidden support person, marked `session.impersonatedByPlatformAdmin`. These routes are
 * authorised by that marker (`authenticatePlatformAdmin` in apps/api), never by a tenant permission
 * — `can()` is untouched and a super admin holds no role expressible in `roles.ts`.
 *
 * `listOrganizations` returns non-sensitive fields only (id, name, slug, status, logo) — never a
 * table with payroll or PII data. It goes through a SECURITY DEFINER Postgres function (see
 * `packages/db/src/bootstrap.ts`, `prisma/sql/rls.sql`) because `hrms_app` is deliberately never
 * privileged.
 *
 * BILLING (Subscriptions & Revenue, offline-payment verification) also lives here: verifying a bank
 * deposit against the company's own statement, and seeing which customers have paid, are
 * platform-owner actions — never a tenant permission (a customer confirming its own payment proves
 * nothing). See billing.contract.ts's header and `OfflinePaymentProof` in schema.prisma.
 *
 * Every route is `ai: 'hidden'`: the copilot derives tools from contract routes, and an assistant
 * must never be handed "switch organisation" as a tool.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  errorBodySchema,
  idempotencyKeySchema,
  moduleKeySchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { tokenPairSchema } from './auth.contract.js';
import { invoiceSchema, offlinePaymentProofSchema, subscriptionSchema } from './billing.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** Non-sensitive summary only — see the file header. Never a field from a tenant table. */
export const organizationSummarySchema = z.object({
  id: orgIdSchema,
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  logoUrl: z.string().nullable(),
  createdAt: z.string(),
});
export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;

export const enterOrganizationResultSchema = z.object({
  tokens: tokenPairSchema,
  organization: z.object({ id: orgIdSchema, name: z.string(), slug: z.string() }),
  actingPerson: z.object({ id: uuidSchema, displayName: z.string() }),
});

/**
 * A submitted offline-payment proof with enough of its invoice and organisation for the review
 * queue to render without a second lookup. Cross-tenant by necessity: a platform owner verifying a
 * deposit against the company's own bank statement spans every organisation's submissions.
 */
export const platformOfflinePaymentReviewSchema = z.object({
  proof: offlinePaymentProofSchema,
  invoice: invoiceSchema,
  organization: z.object({ id: orgIdSchema, name: z.string(), slug: z.string() }),
});
export type PlatformOfflinePaymentReview = z.infer<typeof platformOfflinePaymentReviewSchema>;

/**
 * The "who has paid" view — every organisation, its subscription (null if it never started a
 * checkout) and its most recent invoice. This is the company's OWN billing relationship with a
 * customer, not the customer's payroll/PII, so it is a different category from
 * `organizationSummarySchema`'s "non-sensitive columns only" rule.
 */
export const platformOrgSubscriptionSchema = z.object({
  organization: z.object({ id: orgIdSchema, name: z.string(), slug: z.string() }),
  subscription: subscriptionSchema.nullable(),
  latestInvoice: invoiceSchema.nullable(),
});
export type PlatformOrgSubscription = z.infer<typeof platformOrgSubscriptionSchema>;

/**
 * One organisation's full billing picture for the management portal drill-down: its subscription,
 * recent invoices (each with its offline payment proofs) and the modules it is currently entitled
 * to, tagged with WHY (plan default, purchased add-on, or a manual grant).
 */
export const platformOrgBillingSchema = z.object({
  organization: z.object({ id: orgIdSchema, name: z.string(), slug: z.string() }),
  subscription: subscriptionSchema.nullable(),
  invoices: z.array(invoiceSchema),
  entitlements: z.array(
    z.object({
      moduleKey: z.string(),
      source: z.string(),
      validFrom: z.string(),
      /** Set on `manual_grant` rows only: why the platform owner granted it and who did. */
      reason: z.string().nullable().optional(),
      grantedBy: z.string().nullable().optional(),
    }),
  ),
});
export type PlatformOrgBilling = z.infer<typeof platformOrgBillingSchema>;

/**
 * Which payment gateways are configured on THIS deployment and in which mode, plus the webhook
 * URL to register at each provider. Booleans and public URLs only — never a key or a secret.
 */
export const platformGatewayStatusSchema = z.object({
  razorpay: z.object({
    configured: z.boolean(),
    mode: z.enum(['live', 'test']).nullable(),
    webhookUrl: z.string(),
  }),
  dodo: z.object({
    configured: z.boolean(),
    environment: z.enum(['test_mode', 'live_mode']),
    webhookUrl: z.string(),
  }),
});
export type PlatformGatewayStatus = z.infer<typeof platformGatewayStatusSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listOrganizationsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
});

export const enterOrganizationBodySchema = z.object({
  /** Recorded in the audit grant. Optional: switching companies from the top nav should not need
   *  a form — the grant row (who, which org, when) is written regardless. */
  reason: z.string().trim().min(3).max(500).optional(),
});

export const listPendingOfflinePaymentsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(['pending', 'approved', 'rejected']).default('pending'),
});

export const rejectOfflinePaymentBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

/**
 * `core_hr` is mandatory for every organisation on every plan (it is in every plan's
 * `features.modules.mandatory`), so it is never a manual grant: granting it would be a no-op and
 * removing it would break the tenant. Rejected at the edge (422) rather than in the handler.
 */
export const grantableModuleKeySchema = moduleKeySchema.exclude(['core_hr']);

/** A manual grant/removal always carries a written reason — it is the audit trail. */
const moduleGrantReasonSchema = z.string().trim().min(10).max(500);

export const setOrgModuleGrantBodySchema = z.object({
  moduleKey: grantableModuleKeySchema,
  reason: moduleGrantReasonSchema,
});

export const removeOrgModuleGrantBodySchema = z.object({
  reason: moduleGrantReasonSchema,
});

/** A read-only lock/unlock always carries a written reason too — same audit-trail reasoning as a
 * manual module grant. */
export const setOrgBillingLockBodySchema = z.object({
  locked: z.boolean(),
  reason: moduleGrantReasonSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const errors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
  422: errorBodySchema,
  500: errorBodySchema,
};

/**
 * `null` = "signed in is enough": the adapter's gate (apps/api/src/lib/ts-rest.ts) accepts the
 * platform-owner marker for `null` routes. `authenticatePlatformAdmin` is what actually restricts
 * these to a super admin's session.
 */
const meta = routeMeta(null, { ai: 'hidden' });

export const platformContract = c.router(
  {
    listOrganizations: {
      method: 'GET',
      path: '/organizations',
      query: listOrganizationsQuerySchema,
      responses: { 200: paginatedSchema(organizationSummarySchema), ...errors },
      summary: 'List every organisation on the platform — id/name/slug/status/logo only',
      metadata: meta,
    },

    enterOrganization: {
      method: 'POST',
      path: '/organizations/:orgId/enter',
      pathParams: z.object({ orgId: orgIdSchema }),
      body: enterOrganizationBodySchema,
      responses: { 200: enterOrganizationResultSchema, ...errors },
      summary:
        'Switch into one organisation: opens an audited, time-boxed grant and mints a tenant ' +
        'token pair acting as its hidden support person',
      metadata: meta,
    },

    // -------------------------------------------------------------------------
    // Billing
    // -------------------------------------------------------------------------

    listOrgSubscriptions: {
      method: 'GET',
      path: '/billing/subscriptions',
      responses: { 200: z.object({ items: z.array(platformOrgSubscriptionSchema) }), ...errors },
      summary: 'Every organisation with its subscription and latest invoice — the "who has paid" view',
      metadata: meta,
    },

    getOrgBilling: {
      method: 'GET',
      path: '/billing/subscriptions/:orgId',
      pathParams: z.object({ orgId: orgIdSchema }),
      responses: { 200: platformOrgBillingSchema, ...errors },
      summary: "One organisation's subscription, invoices and entitled modules",
      metadata: meta,
    },

    setOrgModuleGrant: {
      method: 'POST',
      path: '/billing/subscriptions/:orgId/module-grants',
      pathParams: z.object({ orgId: orgIdSchema }),
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: setOrgModuleGrantBodySchema,
      responses: {
        200: platformOrgBillingSchema,
        ...errors,
        /** The module is already open as a manual grant for this organisation. */
        409: errorBodySchema,
      },
      summary:
        'Grant one module to one organisation by hand (source manual_grant), with a mandatory ' +
        'reason. Survives every later subscription sync until removed.',
      metadata: meta,
    },

    removeOrgModuleGrant: {
      method: 'POST',
      path: '/billing/subscriptions/:orgId/module-grants/:moduleKey/remove',
      pathParams: z.object({ orgId: orgIdSchema, moduleKey: grantableModuleKeySchema }),
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: removeOrgModuleGrantBodySchema,
      responses: { 200: platformOrgBillingSchema, ...errors },
      summary:
        'Remove a manual module grant (never a plan or add-on entitlement), with a mandatory reason. ' +
        '404 if there is no open manual grant for that module.',
      metadata: meta,
    },

    setOrgBillingLock: {
      method: 'POST',
      path: '/billing/subscriptions/:orgId/lock',
      pathParams: z.object({ orgId: orgIdSchema }),
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: setOrgBillingLockBodySchema,
      responses: { 200: platformOrgBillingSchema, ...errors },
      summary:
        'Lock an organisation to read-only (every module stays visible and readable, every write ' +
        'refused) or unlock it — an expired trial or a lapsed renewal, today triggered by hand; ' +
        'the automatic day-15 / grace-period sweep is a later phase. Mandatory reason.',
      metadata: meta,
    },

    getPaymentGatewayStatus: {
      method: 'GET',
      path: '/billing/gateways',
      responses: { 200: platformGatewayStatusSchema, ...errors },
      summary: 'Which payment gateways are configured (and live or test), with their webhook URLs',
      metadata: meta,
    },

    listPendingOfflinePayments: {
      method: 'GET',
      path: '/billing/offline-payments',
      query: listPendingOfflinePaymentsQuerySchema,
      responses: { 200: paginatedSchema(platformOfflinePaymentReviewSchema), ...errors },
      summary: 'The cross-organisation offline-payment review queue',
      metadata: meta,
    },

    verifyOfflinePayment: {
      method: 'POST',
      path: '/billing/offline-payments/:proofId/approve',
      pathParams: z.object({ proofId: uuidSchema }),
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: z.object({}),
      responses: {
        200: platformOfflinePaymentReviewSchema,
        ...errors,
        /** Not `pending` — already approved or rejected. */
        409: errorBodySchema,
      },
      summary:
        "Confirm the deposit was found on the company's own bank statement — moves the invoice to " +
        "paid and the organisation's subscription to active",
      metadata: meta,
    },

    rejectOfflinePayment: {
      method: 'POST',
      path: '/billing/offline-payments/:proofId/reject',
      pathParams: z.object({ proofId: uuidSchema }),
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: rejectOfflinePaymentBodySchema,
      responses: {
        200: platformOfflinePaymentReviewSchema,
        ...errors,
        409: errorBodySchema,
      },
      summary: 'Reject a submitted proof — the organisation may resubmit against the same invoice',
      metadata: meta,
    },
  },
  { pathPrefix: '/platform' },
);

export type PlatformContract = typeof platformContract;
